import {UserSession} from "../contract-features-3/domain";
import {LatLng} from "leaflet";
import {FeatureTypeSummary} from "../../../integration/representation/feature-type";
import {Feature, Locational} from "../../../integration/representation/feature";
import {RoadSegment} from "../../../integration/representation/road-segment";
import datacatalog from "../../../integration/datacatalog/datacatalog-services";

export default class LocationalState {
    constructor(
        readonly user: UserSession = null,
        readonly datacatalog: datacatalog = null,
        readonly roadObjectTypes: FeatureTypeSummary[] = [],
        readonly roadObjects: LocationalRoadObject[] = [],
        readonly selectedTypes: number[] = [],
        readonly selectedRoadObjects: string[] = [],
        readonly roadSegments: RoadSegment[] = [],
        readonly selectedRoadSegment: RoadSegment = null,
        readonly progress: number = 0,
        readonly progressGoal: number = 0,
        readonly activeConfidenceFilters: LocationalConfidence[] = [LocationalConfidence.confident, LocationalConfidence.uncertain, LocationalConfidence.missing],
        readonly displaySynchronizedObjects: boolean = false,
        readonly useAnchors: boolean = false,
        readonly listenForAnchorPoint: AnchorPoint = null,
        readonly startAnchor: LatLng = null,
        readonly endAnchor: LatLng = null,
        readonly showAutoLocalizeModal: boolean = false,
        readonly fetchingPointsFromReference: boolean = false,
        readonly detailsModalFeatureId: string = null,
        readonly locationalSelectedForLaneModal: Locational = null,
        readonly selectedOverwriteRoadCategory: string = 'null',
        readonly selectedOverwriteRoadUserGroup: string = 'K',
    ) { }

    private create(overrides: Partial<LocationalState>) {
        const params = {...this, ...overrides};
        const state = new LocationalState(
            params.user,
            params.datacatalog,
            params.roadObjectTypes,
            params.roadObjects,
            params.selectedTypes,
            params.selectedRoadObjects,
            params.roadSegments,
            params.selectedRoadSegment,
            params.progress,
            params.progressGoal,
            params.activeConfidenceFilters,
            params.displaySynchronizedObjects,
            params.useAnchors,
            params.listenForAnchorPoint,
            params.startAnchor,
            params.endAnchor,
            params.showAutoLocalizeModal,
            params.fetchingPointsFromReference,
            params.detailsModalFeatureId,
            params.locationalSelectedForLaneModal,
            params.selectedOverwriteRoadCategory,
            params.selectedOverwriteRoadUserGroup,
        );
        return state;
    }

    public withUser(user: UserSession) {
        return this.create({user});
    }

    public withDatacatalog(datacatalog: datacatalog) {
        return this.create({datacatalog});
    }

    public withFetchingPointsFromReference(fetching: boolean) {
        return this.create({fetchingPointsFromReference: fetching});
    }

    public withShowAutoLocalizeModal(showAutoLocalizeModal: boolean){
        return this.create({showAutoLocalizeModal});
    }

    public withFeatureInDetailsModal(detailsModalFeatureId: string){
        return this.create({detailsModalFeatureId});
    }

    public withListenForAnchorPoint(listenForAnchorPoint: AnchorPoint) {
        return this.create({listenForAnchorPoint})
    }

    public withStartAnchor(startAnchor: LatLng) {
        return this.create({startAnchor})
    }

    public withEndAnchor(endAnchor: LatLng) {
        return this.create({endAnchor})
    }

    public withUseAnchors(useAnchors: boolean) {
        return this.create({useAnchors})
    }

    public withActiveConfidenceFilters(activeConfidenceFilters: LocationalConfidence[]){
        return this.create({activeConfidenceFilters})
    }
    public withDisplaySynchronizedObjects(displaySynchronizedObjects: boolean){
        return this.create({displaySynchronizedObjects});
    }

    public withProgressGoal(progressGoal: number){
        return this.create({progressGoal})
    }

    public withProgress(progress: number){
        return this.create({progress})
    }

    public withRoadObjectTypes(roadObjectTypes: FeatureTypeSummary[]) {
        return this.create({roadObjectTypes});
    }

    public withRoadObjects(roadObjects: LocationalRoadObject[]){
        return this.create({roadObjects});
    }

    public withSelectedTypes(selectedTypes: number[]){
        return this.create({selectedTypes});
    }

    public withSelectedRoadObjects(selectedRoadObjects: string[]){
        return this.create({selectedRoadObjects});
    }

    public withRoadSegments(roadSegments: RoadSegment[]){
        return this.create({roadSegments: roadSegments});
    }

    public withSelectedRoadSegment(selectedRoadSegment: RoadSegment) {
        return this.create({selectedRoadSegment: selectedRoadSegment});
    }

    public withShowLaneModal(locationalSelectedForLaneModal: Locational) {
        return this.create({locationalSelectedForLaneModal: locationalSelectedForLaneModal});
    }

    public withSelectedOverwriteRoadCategory(category: string) {
        return this.create({selectedOverwriteRoadCategory: category});
    }

    public withSelectedOverwriteRoadUserGroup(group: string) {
        return this.create({selectedOverwriteRoadUserGroup: group});
    }
}

export enum LocationalConfidence  {
    missing, uncertain, confident
}

export enum LocationalConfidence_nb_no{
    mangler, usikker, sikker
}

export enum AnchorPoint {
    start, end
}

export interface LocationalRoadObjectType extends FeatureTypeSummary {
    lowestLocationalConfidence: LocationalConfidence
    roadObjects: LocationalRoadObject[],
    isSelected: boolean,
    totalNumberOfRoadObjects: number,
}
export interface LocationalRoadObject extends Feature {
    isSelected: boolean,
    locationalConfidence: LocationalConfidence,
}

export enum SelectedLocalizationType {
    NONE,
    POINT,
    LINE,
    MIXED
}
